import { langMenus, menus } from "@/core/constant/menu"
import { truncateEthAddress } from "@/core/utils/truncateEthAddress"
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined"
import { Drawer, Menu, MenuItem, SvgIcon } from "@mui/material"
import React, { PropsWithChildren, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAccount, useDisconnect, useNetwork } from "wagmi"
import { utils } from "ethers"
import { AvatarBlock } from "./AvatarBlock"
import MenuIcon from "@mui/icons-material/Menu"
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined"
import { ConnectWalletPopup } from "@/components/overview/ConnectWalletPopup"
import AlertContainer from "@/core/store/AlertContainer"
import { SwitchChainPopup } from "@/components/overview/SwitchChainPopup"
import { deleteCookie, getCookie } from "@/core/utils/cookieUtils"
import { useAuth } from "@/core/store/AuthContext"

export type TopBarProps = {}

export const TopBar: React.FunctionComponent<
  PropsWithChildren<TopBarProps>
> = () => {
  const { t, i18n } = useTranslation()
  const { address } = useAccount()
  const { chain } = useNetwork()
  const { authState, updateAuthState } = useAuth()
  const { disconnect } = useDisconnect()
  const { openDialog, closeDialog } = AlertContainer.useContainer()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [anchorDrawer, setAnchorDrawer] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const allowedChainId = Number(import.meta.env.VITE_CHAIN_ID)
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const convertShortAddress = (userAddress: string) => {
    return truncateEthAddress(userAddress)
  }

  const walletDisconnect = () => {
    deleteCookie("jwt-token")
    localStorage.clear()
    updateAuthState({ sign: "", nonce: "" })
    disconnect()
  }

  const walletConnect = () => {
    disconnect()
    openDialog({
      children: (
        <ConnectWalletPopup
          handleClose={closeDialog}
          updateAuthState={updateAuthState}
        />
      ),
      close: closeDialog,
    })
  }

  const isSign =
    (address &&
      authState.nonce &&
      authState.sign &&
      utils.verifyMessage(
        `I am signing with nonce: ${authState.nonce}`,
        authState.sign
      ) === address) ||
    getCookie("jwt-token")

  useEffect(() => {
    if (chain) {
      if (chain.id !== allowedChainId) {
        openDialog({
          children: (
            <SwitchChainPopup handleClose={closeDialog} chain={chain} />
          ),
          close: () => null,
        })
      } else {
        closeDialog()
      }
    }
  }, [chain])

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang).catch()
    handleClose()
  }

  const getLanguage = () => {
    switch (i18n.language) {
      case "en":
        return t("en-US")
      case "ja":
        return t("ja-JP")
      default:
        return ""
    }
  }

  const getMenus = () => {
    return menus
  }

  useEffect(() => {
    const provider = window.diverWallet || window.ethereum
    if (!provider) return

    provider.on("accountsChanged", walletDisconnect)
    provider.on("chainChanged", walletDisconnect)

    return () => {
      provider.removeListener("accountsChanged", walletDisconnect)
      provider.removeListener("chainChanged", walletDisconnect)
    }
  }, [])

  return (
    <div className="sticky z-50 top-0 left-0 flex justify-between items-center w-full px-5 xl:px-10 h-24 flex-0 shadow-md bg-white">
      <div
        className="w-2/3 md:w-1/3 lg:w-1/4 lg:max-w-[486px] cursor-pointer"
        onClick={() => navigate("/")}
      >
        <img
          className="object-fill max-h-full max-w-full"
          src={"../images/logo_w.svg"}
          alt="DIVER_Domain"
        />
      </div>
      <div
        className="h-full lg:hidden inline-flex items-center pr-5"
        onClick={event => setAnchorDrawer(event.currentTarget)}
      >
        <MenuIcon className="text-primary-800" sx={{ fontSize: 40 }} />
      </div>
      <div className="h-full hidden lg:inline-flex items-center">
        {getMenus().map((menu, index) => {
          return (
            <div
              key={index}
              className="inline-flex items-center mr-8 font-semibold cursor-pointer text-primary-dark relative menu-item"
              onClick={() => navigate(menu.path)}
            >
              <SvgIcon component={menu.icon} />
              <span className="pl-1">{t(menu.name)}</span>
            </div>
          )
        })}
        <div
          className="inline-flex items-center mr-8 font-semibold border-l border-r px-5 border-black h-1/2 text-primary-dark hover:text-primary-main cursor-pointer"
          onClick={handleClick}
        >
          <LanguageOutlinedIcon />
          <span className="pl-1">{getLanguage()}</span>
        </div>
        {address && isSign && (
          <div className="inline-flex items-center cursor-pointer">
            <div className="rounded-full overflow-hidden">
              <AvatarBlock seed={address} />
            </div>
            <div className="px-2 text-sm inline-flex flex-col">
              <div className="text-primary-dark">
                {convertShortAddress(address)}
              </div>
              <div className="text-gray-400">
                {chain?.name || "Unknown Chain"}
              </div>
            </div>
          </div>
        )}
        {isSign ? (
          <div
            className="border p-2 border-black text-sm ml-5 cursor-pointer hover:bg-gray-100 font-medium"
            onClick={walletDisconnect}
          >
            {t("disconnect")}
          </div>
        ) : (
          <div
            className="border rounded-full border-primary-dark text-sm px-3 py-2 text-primary-dark font-semibold cursor-pointer hover:bg-primary-dark hover:text-white"
            onClick={walletConnect}
          >
            {t("str_connect_wallet")}
          </div>
        )}
      </div>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {langMenus.map((lang, index) => (
          <MenuItem
            key={index}
            className="min-w-[200px]"
            onClick={() => changeLanguage(lang.translation)}
          >
            {t(lang.name)}
          </MenuItem>
        ))}
      </Menu>
      <Drawer
        anchor="right"
        open={Boolean(anchorDrawer)}
        onClose={() => setAnchorDrawer(null)}
      >
        <div className="w-screen h-full py-10 px-5 flex items-center flex-col justify-center">
          {getMenus().map((menu, index) => {
            return (
              <div
                key={index}
                className="inline-flex items-center my-6 text-3xl font-semibold cursor-pointer text-primary-dark relative menu-item"
                onClick={() => {
                  setAnchorDrawer(null)
                  navigate(menu.path)
                }}
              >
                <SvgIcon component={menu.icon} sx={{ fontSize: 28 }} />
                <span className="pl-1">{t(menu.name)}</span>
              </div>
            )
          })}
          {isSign ? (
            <div
              className="border p-2 border-black text-2xl my-6 px-8 py-3 bg-gray-100 font-medium"
              onClick={walletDisconnect}
            >
              {t("disconnect")}
            </div>
          ) : (
            <div
              className="rounded-full bg-primary-dark text-2xl my-6 py-4 px-8 text-white font-bold"
              onClick={walletConnect}
            >
              {t("str_connect_wallet")}
            </div>
          )}
          <select
            className="text-2xl my-6 px-5 py-2 font-medium"
            value={i18n.language}
            onChange={event => changeLanguage(event.currentTarget.value)}
          >
            {langMenus.map((lang, index) => (
              <option key={index} value={lang.translation}>
                {t(lang.name)}
              </option>
            ))}
          </select>
          <div
            className="text-center text-gray-400 my-6"
            onClick={() => setAnchorDrawer(null)}
          >
            <HighlightOffOutlinedIcon sx={{ fontSize: 36 }} />
          </div>
        </div>
      </Drawer>
    </div>
  )
}
